import { useId } from 'react';
import { Button, CloseIcon, DefaultWarningButtonIcon, Dialog, InputText, InputTextarea } from '@repo/atomic-components';
import { Field, Form } from 'react-final-form';
import type { FormProps } from 'react-final-form';
import { keepFocusOnPress } from '@/utils/keepFocusOnPress';

export type ToolkitPromptFormValues = {
  toolName: string;
  toolDescription: string;
  prompt: string;
};

export type ToolkitPromptFormSubmitHandler = NonNullable<FormProps<ToolkitPromptFormValues>['onSubmit']>;

export interface ToolkitPromptFormModalProps {
  visible: boolean;
  title: string;
  onHide: () => void;
  initialValues: ToolkitPromptFormValues;
  onSubmit: ToolkitPromptFormSubmitHandler;
  isSubmitting?: boolean;
  /** Pass a stable identifier to remount the form when switching subjects (e.g. editing a different tool). */
  formKey?: string | number;
  submitLabel?: string;
}

const validate = (values: ToolkitPromptFormValues) => {
  const errors: Partial<Record<keyof ToolkitPromptFormValues, string>> = {};

  if (!values.toolName?.trim()) errors.toolName = 'Tool name is required';
  if (!values.toolDescription?.trim()) errors.toolDescription = 'Tool description is required';
  if (!values.prompt?.trim()) errors.prompt = 'Prompt is required';

  return errors;
};

export const ToolkitPromptFormModal = ({
  visible,
  title,
  onHide,
  initialValues,
  onSubmit,
  isSubmitting = false,
  formKey,
  submitLabel = 'Confirm',
}: ToolkitPromptFormModalProps) => {
  // Create and edit are separate instances, so the field ids are per-instance.
  const fieldIdPrefix = useId();

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      modal
      draggable={false}
      closable={false}
      header={
        <div className="text-theme-black flex w-full items-start justify-between gap-4">
          <span className="text-[1.25rem] leading-7.5 font-semibold">{title}</span>
          <Button
            type="button"
            onMouseDown={keepFocusOnPress}
            onClick={onHide}
            outlined
            className="text-theme-black hover:text-theme-black flex h-8 w-8 shrink-0 items-center justify-center rounded border-none transition-colors hover:bg-transparent"
            aria-label="Close"
          >
            <CloseIcon className="h-[14px] w-[14px]" />
          </Button>
        </div>
      }
      className="!w-[min(42.5rem,calc(100vw-48px))]"
      pt={{
        root: {
          className: 'rounded-lg dialog-shadow flex max-h-[min(40rem,calc(100vh-48px))] flex-col',
        },
        header: {
          className:
            'min-h-[4.625rem] cursor-default !rounded-t-lg bg-theme-white text-theme-black border-theme-border-grey shrink-0 border-b px-6 py-4',
        },
        headerTitle: { className: '!m-0 !w-full' },
        content: {
          className: 'flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-theme-white px-6 pt-4 pb-6 rounded-b-lg',
        },
        closeButton: { className: 'hidden' },
      }}
    >
      <Form<ToolkitPromptFormValues>
        key={formKey ?? String(visible)}
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={validate}
      >
        {({ handleSubmit, invalid, submitting }) => (
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col gap-2">
            <Field<string> name="toolName">
              {({ input, meta }) => (
                <div className="flex flex-col gap-2">
                  {/* A placeholder is not an accessible name (WCAG 3.3.2), and edit opens
                    with the field already filled. */}
                  <label htmlFor={`${fieldIdPrefix}-tool-name`} className="sr-only">
                    Tool name
                  </label>
                  <InputText
                    id={`${fieldIdPrefix}-tool-name`}
                    className="!text-theme-black !border-theme-border-grey !bg-theme-white h-8 w-full rounded-lg !text-sm placeholder:!text-sm"
                    placeholder="Tool name"
                    value={input.value}
                    onChange={(e) => input.onChange(e.target.value)}
                    onBlur={input.onBlur}
                    onFocus={input.onFocus}
                  />
                  {meta.touched && meta.error && !submitting && !isSubmitting ? (
                    <div className="text-xs text-red-600">{meta.error}</div>
                  ) : null}
                </div>
              )}
            </Field>

            <Field<string> name="toolDescription">
              {({ input, meta }) => (
                <div className="flex flex-col gap-2">
                  <label htmlFor={`${fieldIdPrefix}-tool-description`} className="sr-only">
                    Tool description
                  </label>
                  <InputText
                    id={`${fieldIdPrefix}-tool-description`}
                    className="!text-theme-black !border-theme-border-grey !bg-theme-white h-8 w-full rounded-lg !text-sm placeholder:!text-sm"
                    placeholder="Tool description"
                    value={input.value}
                    onChange={(e) => input.onChange(e.target.value)}
                    onBlur={input.onBlur}
                    onFocus={input.onFocus}
                  />
                  {meta.touched && meta.error && !submitting && !isSubmitting ? (
                    <div className="text-xs text-red-600">{meta.error}</div>
                  ) : null}
                </div>
              )}
            </Field>

            <Field<string> name="prompt">
              {({ input, meta }) => (
                <div className="flex min-h-0 flex-1 flex-col gap-2">
                  <label htmlFor={`${fieldIdPrefix}-prompt`} className="sr-only">
                    Prompt
                  </label>
                  <InputTextarea
                    id={`${fieldIdPrefix}-prompt`}
                    rows={6}
                    className="!text-theme-black !border-theme-border-grey !bg-theme-white max-h-[145px] min-h-[145px] w-full resize-none overflow-y-auto rounded-lg !text-sm placeholder:!text-sm"
                    placeholder="Prompt"
                    value={input.value}
                    onChange={(e) => input.onChange(e.target.value)}
                    onBlur={input.onBlur}
                    onFocus={input.onFocus}
                  />
                  {meta.touched && meta.error && !submitting && !isSubmitting ? (
                    <div className="text-xs text-red-600">{meta.error}</div>
                  ) : null}
                </div>
              )}
            </Field>

            <div className="text-theme-text-grey mt-3 flex items-center justify-center gap-2 text-xs">
              <DefaultWarningButtonIcon className="h-[16px] w-[18px] shrink-0" aria-hidden />
              <span>Please make sure this prompt contains no PII before saving.</span>
            </div>

            <div className="flex justify-end pt-1">
              <Button
                type="submit"
                disabled={invalid || submitting || isSubmitting}
                className="bg-theme-dark-green hover:bg-theme-dark-green text-theme-white justify-center disabled:opacity-50"
              >
                {submitLabel}
              </Button>
            </div>
          </form>
        )}
      </Form>
    </Dialog>
  );
};
